"use client";

/**
 * Всплывашка входящего сообщения чата (менеджер/проктор → студент и обратно).
 *
 * Контракт:
 *  - без actions — прежний вид из Nuxt (components/Oqylyq/Chat/ChatComponent.vue):
 *    аватар, имя, текст в одну строку с многоточием. Так видят все бренды, кроме
 *    app.trustexam.ai (см. chatRichMessagesEnabled в lib/branding.ts);
 *  - с actions (.chat-notification--full):
 *    - текст целиком, с переносами строк; длинный прокручивается внутри;
 *    - ссылки кликабельны (см. linkifyText), открываются в новой вкладке;
 *    - клик по всплывашке шлёт window-событие OPEN_EXAM_CHAT_EVENT: экран теста
 *      его ловит и открывает чат экзамена. Если событие кто-то обработал
 *      (preventDefault), вызывается onOpened — всплывашку можно закрыть;
 *    - крестик вызывает onClose.
 * Стили лежат в globals.css под классами .chat-notification.
 */

import { X } from "lucide-react";
import { linkifyText } from "@/components/chat/linkifyText";

export const OPEN_EXAM_CHAT_EVENT = "open-exam-chat";

export type ChatToastUser = {
  firstname?: string | null;
  lastname?: string | null;
  photo?: string | null;
  color?: string | null;
};

type Props = {
  user: ChatToastUser | null;
  text: string;
  actions?: {
    onClose: () => void;
    onOpened: () => void;
    closeLabel: string;
  };
};

function fullName(user: ChatToastUser | null) {
  return [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim();
}

function firstLetter(user: ChatToastUser | null) {
  const name = fullName(user);
  return name ? name[0].toUpperCase() : "?";
}

function requestOpenExamChat() {
  /* dispatchEvent возвращает false, если слушатель сделал preventDefault */
  return !window.dispatchEvent(new Event(OPEN_EXAM_CHAT_EVENT, { cancelable: true }));
}

export function ChatMessageToast({ user, text, actions }: Props) {
  const name = fullName(user);
  const photo = user?.photo && String(user.photo).trim() ? String(user.photo) : null;
  const background = user?.color && String(user.color).trim() ? `#${String(user.color).trim()}` : "#6B7280";

  return (
    <div
      className={actions ? "chat-notification chat-notification--full" : "chat-notification"}
      onClick={
        actions &&
        (() => {
          /* не перехватываем выделение текста мышью */
          if (window.getSelection()?.toString()) return;
          if (requestOpenExamChat()) actions.onOpened();
        })
      }
    >
      <div className="notification-user">
        <div className="notification-user-avatar">
          {photo ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img className="avatar-image avatar-rounded" src={photo} alt={name} />
          ) : (
            <div className="avatar-letter avatar-rounded" style={{ backgroundColor: background }}>
              <span>{firstLetter(user)}</span>
            </div>
          )}
        </div>
        <div className="notification-user-name">{name}</div>
        {actions && (
          <button
            type="button"
            className="notification-close"
            aria-label={actions.closeLabel}
            title={actions.closeLabel}
            onClick={(e) => {
              e.stopPropagation();
              actions.onClose();
            }}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="notification-message">
        {actions ? linkifyText(text, "notification-link") : text}
      </div>
    </div>
  );
}

export default ChatMessageToast;
